import type { BrandTokens, GameEntry, OperatorSettings } from '../types';
import { defaultOperatorSettings, encodeSettings, launchUrl, normalizeSettings, validGameTitle } from '../platform/operator';

export interface AdminOptions {
  root: HTMLElement;
  entry: GameEntry;
  settings: OperatorSettings;
  basePath: string;
  jurisdictions: { id: string; name: string }[];
  brands: { id: string; name: string }[];
  locales: { id: string; name: string }[];
  onApply: (settings: OperatorSettings) => void;
  onReset: () => void;
  onClose?: () => void;
}

type Tab = 'game' | 'compliance' | 'branding' | 'integration';

/** Sensible starting colours when an operator enables a token override (Lucky Fruit / Emerald defaults). */
const TOKEN_DEFAULTS: Record<keyof BrandTokens, string> = {
  page: '#070b12',
  pageInk: '#f6f8fb',
  machine: '#111924',
  machineDeep: '#0a1017',
  panel: '#141d28',
  panelSoft: '#1b2734',
  primary: '#00ef9b',
  primaryInk: '#02120d',
  accent: '#56f5c5',
  line: '#2c3d4c',
  loss: '#ff405c',
};

const TOKEN_KEYS: { key: keyof BrandTokens; label: string }[] = [
  { key: 'primary', label: 'Primary' },
  { key: 'primaryInk', label: 'Primary ink' },
  { key: 'accent', label: 'Accent' },
  { key: 'page', label: 'Page' },
  { key: 'pageInk', label: 'Text' },
  { key: 'machine', label: 'Machine' },
  { key: 'panel', label: 'Panel' },
  { key: 'panelSoft', label: 'Panel soft' },
  { key: 'line', label: 'Line' },
  { key: 'loss', label: 'Loss' },
];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}


/** Plain-language help for every operator setting (shown as a tooltip on hover / focus). */
const HELP: Record<string, string> = {
  'RTP / math variant':
    'Which certified maths the game runs on. RTP is the share of all bets the game returns to players over the long run (96% means players get back about 96 of every 100 wagered on average). Each variant is a separate paytable and reel set; switching it changes the odds and pays but not the artwork. Pick the version allowed in your market.',
  'Currency code':
    'The ISO code stored with every bet and win (for example EUR, USD, FUN for play money). It is shown to players only where a code is expected and is passed to your wallet and reports. Leave empty to keep the game default.',
  'Currency symbol':
    'The sign printed next to amounts on screen: balance, bet, wins and the paytable. Use € for euro, $ for dollars, ₽ for roubles. It only changes how money looks, never the values.',
  'Symbol position':
    'Where the currency sign sits: before the number ($1.00, English style) or after it with a space (1,00 €, most European markets). Suffix mode also switches to the comma decimal separator.',
  'Decimals':
    'How many digits are shown after the decimal point. 2 is standard for fiat money (1.50); 0 suits currencies without cents or for coin-style play. Only display changes; bets and wins keep full precision.',
  'Demo balance':
    'The starting play-money balance for this demo. It is only used here in the showcase and in demo (fun) mode; in a real deployment the balance comes from your wallet.',
  'Bet list (comma separated, empty = math default)':
    'The bets a player can step through with the + and − buttons, as total bet per spin. List them separated by commas (0.2, 0.5, 1, 2, 5). Values outside what the maths allows are clamped. Empty keeps the list defined by the maths.',
  'Default bet':
    'The bet selected when a player opens the game for the first time. Must be one of the bets in the list; empty picks the maths default (usually the lowest or a mid value).',
  'Max win cap (× bet)':
    'Hard ceiling on what one spin can pay, as a multiple of the total bet (for example 2500 = a €1 bet can never pay more than €2500). Anything above is cut to the cap. Empty keeps the cap certified with the maths.',
  'Jurisdiction profile':
    'A bundle of regulatory rules for a market: minimum spin time, whether turbo, quick stop, autoplay and bonus buy are allowed, session clock and reality checks. Choosing UK, Germany, Ontario or Netherlands applies that regulator\u2019s requirements; the toggles below can override single rules.',
  'Language':
    'Language of all in-game text: buttons, paytable, rules, result plaques. The showcase itself stays in English; only the game changes.',
  'Quick spin (turbo)':
    'Lets players shorten the reel animation so rounds finish faster. Some regulators forbid it (UK, Ontario) because it speeds up play. Default follows the jurisdiction; On/Off forces it.',
  'Quick stop (slam)':
    'Lets players press the button again while reels are spinning to stop them immediately. When it is off, the button shows SPINNING… and waits for the round to finish. Default follows the jurisdiction.',
  'Autoplay':
    'Adds an autoplay mode that runs several spins in a row without pressing the button each time. Banned in several regulated markets. Default follows the jurisdiction.',
  'Bonus buy':
    'Lets players pay a fixed multiple of the bet to enter the free-spins feature immediately instead of waiting for scatters. Only applies to games with free spins; banned in some markets (UK, Netherlands).',
  'Celebrate wins below stake':
    'Whether a win smaller than the bet (a net loss) gets the same lights and sounds as a real win. Regulators such as the UKGC require these to stay quiet; the win is still shown in numbers.',
  'Session clock':
    'Shows how long the player has been playing in the current session, in the HUD. Required in several markets as a responsible-gambling measure.',
  'Net position display':
    'Shows the player\u2019s net result for the session (total wins minus total bets) in the HUD, so it is always clear whether they are up or down.',
  'Reality check interval':
    'Every N minutes the game pauses and shows a summary (time played, wagered, won) that the player must acknowledge before continuing. 0 turns it off, empty keeps the jurisdiction default.',
  'Sound on by default':
    'Whether sound starts enabled when the game opens. Players can always toggle it with the speaker button; this only sets the initial state.',
  'Brand preset':
    'A ready-made interface skin: colours of the HUD and buttons, fonts, button shapes, logo placement. The game art (symbols, background, mascot) stays the same; only the chrome around it changes. Design tokens below fine-tune the chosen preset.',
  'Game title':
    'The name shown in the game header and on the loading screen. Leave it empty or equal to the game\u2019s own name to show the illustrated logo with the CLEFDEV.COM signature. Any other name (1–40 characters) is rendered as text with the themed signature underneath.',
  'Logo URL':
    'Your own logo image (PNG or SVG by URL), shown in the top-left corner over the game. Optional; it does not replace the game logo.',
  'HUD style':
    'How the control panel looks: Bar is a full-width strip along the bottom, Floating is a rounded panel inset from the edges, Minimal hides labels and shows only the essentials.',
  'HUD position':
    'Whether the control panel sits at the bottom (standard) or the top of the game area.',
  'Spin button art':
    'Themed art means the illustrated spin button that matches the game (three states: normal, pressed, disabled). Plain button uses a flat button drawn from the brand colours, styled by the shape and style settings below.',
  'Spin button shape':
    'Shape of the plain spin button: circle, rounded square or pill. Ignored while themed art is active.',
  'Spin button style':
    'Fill of the plain spin button: flat colour, gradient, or outline only. Ignored while themed art is active.',
  'Spin label':
    'Custom text on the spin button (for example GO or PLAY). Empty keeps the localised SPIN.',
  'Design tokens':
    'Single colours that override the brand preset. Tick a token to use it. Primary and Accent drive buttons and highlights, Page and Machine colour the backgrounds, Panel and Panel soft colour the HUD, Line the separators, Loss the negative numbers.',
  'Primary': 'Main action colour: the plain spin button, primary buttons and win highlights.',
  'Primary ink': 'Text colour placed on top of the primary colour (must contrast with it).',
  'Accent': 'Secondary highlight colour: paytable headings, free-spins counter, focus rings.',
  'Page': 'Background colour behind the game when no background image covers the screen.',
  'Text': 'Default text colour of the interface (readouts, labels).',
  'Machine': 'Backdrop colour inside the reel window behind the symbols.',
  'Panel': 'Background of the control panel (HUD) and dialogs.',
  'Panel soft': 'Lighter variant used for secondary buttons and chips.',
  'Line': 'Colour of separators, borders and the lines between reels.',
  'Loss': 'Colour of negative numbers such as a negative net position.',
  'Launch URL (current settings)':
    'The address a platform opens to run this game with exactly the settings above baked in. Paste it into an iframe or open it in a new tab. In production the settings would live in your back office and the URL would carry only a session token.',
  'Iframe embed':
    'Ready-to-paste HTML that embeds the game in any page. The game needs autoplay and fullscreen permissions and a container of a sensible size (16:9 on desktop, 390×844 on phones).',
  'JS embed (same settings)':
    'The same launch without an iframe: the JavaScript library mounts the game into a <div> on your page and takes a wallet adapter (getBalance, debit, credit) so real balances flow through your system.',
  'Bridge messages (iframe ↔ platform)':
    'Events the game sends to the page that embeds it (ready, idle, busy, balance, spin, state, error) and commands the page can send back (pause, resume, set balance, get state, switch theme or brand). Use them for analytics, session control and wallet sync.',
  'Settings JSON':
    'Everything configured in this panel as a single JSON document. Store it per operator and game in your back office; it is what the launch URL encodes.',
};

/** Small "?" next to a label; the description appears on hover or keyboard focus. */
function helpIcon(text: string): HTMLElement {
  const b = el('button', 'sc-help');
  b.type = 'button';
  b.setAttribute('aria-label', 'What is this?');
  b.textContent = '?';
  const tip = el('span', 'sc-tip', text);
  tip.setAttribute('role', 'tooltip');
  b.appendChild(tip);
  return b;
}

function field(label: string, input: HTMLElement, wide = false): HTMLLabelElement {
  const f = el('label', `sc-field${wide ? ' sc-field--wide' : ''}`);
  const cap = el('span', undefined, label);
  const help = HELP[label];
  if (help) {
    cap.appendChild(helpIcon(help));
    if ('setAttribute' in input) input.setAttribute('title', help);
  }
  f.append(cap, input);
  return f;
}

/** Section label with the same tooltip mechanism as fields. */
function labelWithHelp(cls: string, text: string): HTMLElement {
  const p = el('p', cls, text);
  const help = HELP[text];
  if (help) p.appendChild(helpIcon(help));
  return p;
}

function select(options: { value: string; label: string }[], value: string): HTMLSelectElement {
  const s = el('select');
  for (const o of options) {
    const opt = el('option', undefined, o.label);
    opt.value = o.value;
    if (o.value === value) opt.selected = true;
    s.appendChild(opt);
  }
  return s;
}

/** default / on / off select bound to a nullable boolean. */
function triState(value: boolean | null): HTMLSelectElement {
  return select(
    [
      { value: '', label: 'Default (jurisdiction)' },
      { value: '1', label: 'On' },
      { value: '0', label: 'Off' },
    ],
    value == null ? '' : value ? '1' : '0',
  );
}
const readTri = (s: HTMLSelectElement): boolean | null => (s.value === '' ? null : s.value === '1');

function input(type: string, value: string, placeholder = ''): HTMLInputElement {
  const i = el('input');
  i.type = type;
  i.value = value;
  i.placeholder = placeholder;
  return i;
}

/** Operator admin panel for one game (modal). Returns a close function. */
export function openAdmin(o: AdminOptions): () => void {
  const s = normalizeSettings(o.entry.id, JSON.parse(JSON.stringify(o.settings)) as OperatorSettings);
  const overlay = el('div', 'sc-overlay');
  const modal = el('div', 'sc-modal');
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'sc-admin-title');
  const head = el('div', 'sc-modal__head');
  const title = el('div');
  const h2 = el('h2', undefined, `${o.entry.name} — operator settings`);
  h2.id = 'sc-admin-title';
  title.append(h2, el('small', undefined, `Game id: ${o.entry.id}. Settings are stored per game and can be encoded into a launch URL.`));
  const close = el('button', 'sc-modal__close', '×');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close');
  head.append(title, close);

  const tabs = el('div', 'sc-tabs');
  tabs.setAttribute('role', 'tablist');
  tabs.setAttribute('aria-label', 'Settings sections');
  const body = el('div', 'sc-modal__body');
  body.setAttribute('role', 'tabpanel');
  body.id = 'sc-admin-panel';
  body.tabIndex = 0;
  const panels: Record<Tab, HTMLElement> = { game: el('div'), compliance: el('div'), branding: el('div'), integration: el('div') };
  const tabDefs: { id: Tab; label: string }[] = [
    { id: 'game', label: 'Game' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'branding', label: 'Branding' },
    { id: 'integration', label: 'Integration' },
  ];
  let active: Tab = 'game';
  const showTab = (id: Tab) => {
    active = id;
    for (const b of tabs.querySelectorAll('button')) {
      const on = b.dataset.tab === id;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', String(on));
      b.tabIndex = on ? 0 : -1;
    }
    body.replaceChildren(panels[id]);
    body.setAttribute('aria-labelledby', `sc-admin-tab-${id}`);
    if (id === 'integration') renderIntegration();
  };
  for (const t of tabDefs) {
    const b = el('button', undefined, t.label);
    b.type = 'button';
    b.dataset.tab = t.id;
    b.setAttribute('role', 'tab');
    b.id = `sc-admin-tab-${t.id}`;
    b.setAttribute('aria-controls', 'sc-admin-panel');
    b.addEventListener('click', () => showTab(t.id));
    b.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const i = tabDefs.findIndex((x) => x.id === active);
      const next = tabDefs[(i + (e.key === 'ArrowRight' ? 1 : tabDefs.length - 1)) % tabDefs.length];
      showTab(next.id);
      tabs.querySelector<HTMLButtonElement>(`[data-tab="${next.id}"]`)?.focus();
    });
    tabs.appendChild(b);
  }

  // ---------------- Game tab
  const g = el('div', 'sc-grid');
  const variants = o.entry.mathVariants?.length ? o.entry.mathVariants : [{ id: o.entry.math ?? 'default', label: o.entry.math ?? 'default' }];
  const mathSel = select(variants.map((v) => ({ value: v.id, label: v.label })), s.math ?? variants[0].id);
  const curCode = input('text', s.currency.code ?? '', 'FUN');
  const curSymbol = input('text', s.currency.symbol ?? '', '$');
  const curPos = select([{ value: '', label: 'Default' }, { value: 'prefix', label: 'Prefix ($1.00)' }, { value: 'suffix', label: 'Suffix (1,00 €)' }], s.currency.position ?? '');
  const curDec = select([{ value: '', label: 'Default (2)' }, { value: '0', label: '0' }, { value: '1', label: '1' }, { value: '2', label: '2' }], s.currency.decimals == null ? '' : String(s.currency.decimals));
  const balance = input('number', String(s.demoBalance));
  const bets = input('text', s.bets.list ? s.bets.list.join(', ') : '', 'e.g. 0.2, 0.5, 1, 2, 5');
  const defBet = input('number', s.bets.defaultBet == null ? '' : String(s.bets.defaultBet), 'default');
  const maxWin = input('number', s.maxWinMultiplier == null ? '' : String(s.maxWinMultiplier), 'x bet, default from math');
  g.append(
    field('RTP / math variant', mathSel, true),
    field('Currency code', curCode),
    field('Currency symbol', curSymbol),
    field('Symbol position', curPos),
    field('Decimals', curDec),
    field('Demo balance', balance),
    field('Bet list (comma separated, empty = math default)', bets, true),
    field('Default bet', defBet),
    field('Max win cap (× bet)', maxWin),
  );
  panels.game.append(el('p', 'sc-hint', 'Operators pick the certified RTP version and the bet configuration per currency/market. Bets must be a subset the math allows; the engine clamps out-of-range values.'), g);

  // ---------------- Compliance tab
  const c = el('div', 'sc-grid');
  const jur = select(o.jurisdictions.map((j) => ({ value: j.id, label: j.name })), s.jurisdiction);
  const loc = select(o.locales.map((l) => ({ value: l.id, label: l.name })), s.locale);
  const f = s.features;
  const quickSpin = triState(f.quickSpin);
  const quickStop = triState(f.quickStop);
  const autoplay = triState(f.autoplay);
  const bonusBuy = triState(f.bonusBuy);
  const celebrate = triState(f.celebrateBelowStake);
  const clock = triState(f.sessionClock);
  const net = triState(f.netPosition);
  const reality = input('number', f.realityCheckMinutes == null ? '' : String(f.realityCheckMinutes), 'minutes, 0 = off, empty = default');
  const sound = triState(f.sound);
  c.append(
    field('Jurisdiction profile', jur),
    field('Language', loc),
    field('Quick spin (turbo)', quickSpin),
    field('Quick stop (slam)', quickStop),
    field('Autoplay', autoplay),
    field('Bonus buy', bonusBuy),
    field('Celebrate wins below stake', celebrate),
    field('Session clock', clock),
    field('Net position display', net),
    field('Reality check interval', reality),
    field('Sound on by default', sound),
  );
  panels.compliance.append(el('p', 'sc-hint', 'The jurisdiction profile sets the regulatory baseline (min spin time, stake caps, bans). Toggles override single flags; the engine enforces them, not just the UI.'), c);

  // ---------------- Branding tab
  const b = el('div', 'sc-grid');
  const brandSel = select([{ value: '', label: `Game default (${o.entry.brand ?? 'none'})` }, ...o.brands.map((x) => ({ value: x.id, label: x.name }))], s.brand ?? '');
  const tokenInputs = new Map<keyof BrandTokens, { color: HTMLInputElement; on: HTMLInputElement }>();
  const tokensGrid = el('div', 'sc-tokens');
  for (const tk of TOKEN_KEYS) {
    const wrap = el('label', 'sc-token');
    const row = el('div', 'sc-token__row');
    const on = input('checkbox', '');
    on.checked = s.branding.tokens[tk.key] != null;
    on.setAttribute('aria-label', `Override ${tk.label}`);
    const color = input('color', s.branding.tokens[tk.key] ?? TOKEN_DEFAULTS[tk.key]);
    color.disabled = !on.checked;
    on.addEventListener('change', () => (color.disabled = !on.checked));
    row.append(on, color);
    wrap.append(el('span', 'sc-token__label', tk.label), row);
    const tipText = HELP[tk.label];
    if (tipText) wrap.querySelector('.sc-token__label')?.appendChild(helpIcon(tipText));
    tokensGrid.appendChild(wrap);
    tokenInputs.set(tk.key, { color, on });
  }
  const logo = input('url', s.branding.logo ?? '', 'https://…/logo.svg (shown top-left)');
  const gameTitle = input('text', s.branding.title ?? '', `${o.entry.name} (1–40 characters; a different name shows as text + CLEFDEV.COM signature)`);
  gameTitle.maxLength = 40;
  const hudStyle = select([{ value: '', label: 'Brand default' }, { value: 'bar', label: 'Bar' }, { value: 'floating', label: 'Floating' }, { value: 'minimal', label: 'Minimal' }], s.branding.hudStyle ?? '');
  const hudPos = select([{ value: '', label: 'Brand default' }, { value: 'bottom', label: 'Bottom' }, { value: 'top', label: 'Top' }], s.branding.hudPosition ?? '');
  const spinShape = select([{ value: '', label: 'Brand default' }, { value: 'circle', label: 'Circle' }, { value: 'rounded', label: 'Rounded' }, { value: 'pill', label: 'Pill' }], s.branding.spinShape ?? '');
  const spinStyle = select([{ value: '', label: 'Brand default' }, { value: 'flat', label: 'Flat' }, { value: 'gradient', label: 'Gradient' }, { value: 'outline', label: 'Outline' }], s.branding.spinStyle ?? '');
  const spinLabel = input('text', s.branding.strings.spin ?? '', 'SPIN');
  const spinArt = select([{ value: '', label: 'Brand default (themed art when the theme has it)' }, { value: '1', label: 'Themed art (PNG frames)' }, { value: '0', label: 'Plain button (shape / style below)' }], s.branding.spinArt == null ? '' : s.branding.spinArt ? '1' : '0');
  b.append(field('Brand preset', brandSel, true), field('Game title', gameTitle, true), field('Logo URL', logo, true), field('HUD style', hudStyle), field('HUD position', hudPos), field('Spin button art', spinArt), field('Spin button shape', spinShape), field('Spin button style', spinStyle), field('Spin label', spinLabel));
  panels.branding.append(
    el('p', 'sc-hint', 'Brand presets carry the full chrome (colours, fonts, buttons). Tokens below override single colours of the preset — tick a token to use it.'),
    b,
    labelWithHelp('sc-eyebrow', 'Design tokens'),
    tokensGrid,
  );

  // ---------------- Integration tab
  const integ = panels.integration;
  const collect = (): OperatorSettings => {
    const parseList = (v: string) => {
      const nums = v.split(/[\s,;]+/).map(Number).filter((n) => Number.isFinite(n) && n > 0);
      return nums.length ? nums : null;
    };
    const tokens: BrandTokens = {};
    for (const [k, v] of tokenInputs) if (v.on.checked) tokens[k] = v.color.value;
    const num = (i: HTMLInputElement) => (i.value.trim() === '' ? null : Number(i.value));
    return normalizeSettings(o.entry.id, {
      math: mathSel.value === (o.entry.math ?? 'default') ? null : mathSel.value,
      brand: brandSel.value || null,
      jurisdiction: jur.value,
      locale: loc.value,
      currency: {
        ...(curCode.value.trim() ? { code: curCode.value.trim() } : {}),
        ...(curSymbol.value !== '' ? { symbol: curSymbol.value } : {}),
        ...(curPos.value ? { position: curPos.value as 'prefix' | 'suffix' } : {}),
        ...(curDec.value !== '' ? { decimals: Number(curDec.value) } : {}),
      },
      demoBalance: Number(balance.value) || 1000,
      bets: { list: parseList(bets.value), defaultBet: num(defBet) },
      maxWinMultiplier: num(maxWin),
      features: {
        quickSpin: readTri(quickSpin),
        quickStop: readTri(quickStop),
        autoplay: readTri(autoplay),
        bonusBuy: readTri(bonusBuy),
        celebrateBelowStake: readTri(celebrate),
        sessionClock: readTri(clock),
        netPosition: readTri(net),
        realityCheckMinutes: num(reality),
        sound: readTri(sound),
      },
      branding: {
        tokens,
        logo: logo.value.trim() || null,
        title: validGameTitle(gameTitle.value),
        hudStyle: (hudStyle.value || null) as OperatorSettings['branding']['hudStyle'],
        hudPosition: (hudPos.value || null) as OperatorSettings['branding']['hudPosition'],
        spinShape: (spinShape.value || null) as OperatorSettings['branding']['spinShape'],
        spinStyle: (spinStyle.value || null) as OperatorSettings['branding']['spinStyle'],
        spinArt: spinArt.value === '' ? null : spinArt.value === '1',
        strings: spinLabel.value.trim() ? { spin: spinLabel.value.trim() } : {},
      },
    });
  };
  const renderIntegration = () => {
    const cur = collect();
    const url = launchUrl(o.basePath, cur);
    integ.replaceChildren();
    integ.append(
      el('p', 'sc-hint', 'How a platform launches this game: open the launch URL in an iframe (or mount the JS bundle). The settings above are encoded in the URL; in production they live in your back-office and the URL only carries a session token.'),
      labelWithHelp('sc-label', 'Launch URL (current settings)'),
      Object.assign(el('code', 'sc-code', url), { id: 'sc-launch-url' }),
      labelWithHelp('sc-label', 'Iframe embed'),
      el('code', 'sc-code', `<iframe src="${url}" allow="autoplay; fullscreen" style="width:100%;height:100vh;border:0"></iframe>`),
      labelWithHelp('sc-label', 'JS embed (same settings)'),
      el('code', 'sc-code', `SimpleSlot.createSlot(el, ${JSON.stringify({ theme: o.entry.theme, math: cur.math ?? o.entry.math, brand: cur.brand ?? o.entry.brand, jurisdiction: cur.jurisdiction, locale: cur.locale, wallet: '/* your seamless-wallet adapter */' }, null, 1).replace('"/* your seamless-wallet adapter */"', '{ getBalance, debit, credit }')})`),
      labelWithHelp('sc-label', 'Bridge messages (iframe ↔ platform)'),
      el('code', 'sc-code', 'game → host: slot:ready, slot:idle, slot:busy, slot:balance, slot:spin, slot:state, slot:error\nhost → game: host:pause, host:resume, host:setBalance, host:getState, host:setTheme, host:setBrand'),
      labelWithHelp('sc-label', 'Settings JSON'),
      el('code', 'sc-code', JSON.stringify(cur, null, 1)),
    );
    const row = el('div', 'sc-row');
    const copy = el('button', 'sc-btn sc-btn--primary sc-btn--small', 'Copy launch URL');
    copy.type = 'button';
    copy.addEventListener('click', () => void navigator.clipboard?.writeText(url).then(() => toast(o.root, 'Launch URL copied')));
    const copyJson = el('button', 'sc-btn sc-btn--small', 'Copy settings JSON');
    copyJson.type = 'button';
    copyJson.addEventListener('click', () => void navigator.clipboard?.writeText(JSON.stringify(cur, null, 2)).then(() => toast(o.root, 'Settings copied')));
    row.append(copy, copyJson);
    integ.appendChild(row);
    void encodeSettings;
  };

  // ---------------- footer
  const foot = el('div', 'sc-modal__foot');
  const left = el('div');
  const reset = el('button', 'sc-btn sc-btn--ghost sc-btn--danger', 'Reset to defaults');
  reset.type = 'button';
  left.appendChild(reset);
  const right = el('div');
  const cancel = el('button', 'sc-btn sc-btn--ghost', 'Cancel');
  cancel.type = 'button';
  const apply = el('button', 'sc-btn sc-btn--primary', 'Apply & reload game');
  apply.type = 'button';
  right.append(cancel, apply);
  foot.append(left, right);

  modal.append(head, tabs, body, foot);
  overlay.appendChild(modal);
  o.root.appendChild(overlay);
  showTab(active);
  const opener = document.activeElement as HTMLElement | null;
  close.focus();

  const closeModal = () => {
    overlay.remove();
    window.removeEventListener('keydown', onKey);
    opener?.focus?.();
    o.onClose?.();
  };
  const focusables = (): HTMLElement[] =>
    [...modal.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter((n) => n.offsetParent !== null);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal();
      return;
    }
    if (e.key !== 'Tab') return;
    const f = focusables();
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  window.addEventListener('keydown', onKey);
  close.addEventListener('click', closeModal);
  cancel.addEventListener('click', closeModal);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal();
  });
  reset.addEventListener('click', () => {
    closeModal();
    o.onReset();
  });
  apply.addEventListener('click', () => {
    const next = collect();
    closeModal();
    o.onApply(next);
  });
  void defaultOperatorSettings;
  return closeModal;
}

export function toast(root: HTMLElement, message: string): void {
  let t = root.querySelector<HTMLDivElement>('.sc-toast');
  if (!t) {
    t = el('div', 'sc-toast');
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    root.appendChild(t);
  }
  t.textContent = message;
  t.classList.add('is-visible');
  setTimeout(() => t?.classList.remove('is-visible'), 1800);
}
